/*
 * CheckoutPage - this page is used to handle shipping and payment step.
 * If stepNumber = 0, the shipping page is showed
 * if stepNumber = 1, the payment step is showed
*/

import { useState, useEffect, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from "@redux-actions/";
import { getLabelFallback, convertNumber, getTotalTax, getSubtotal, getDiscoutCodes, getDiscountNames, handleHTTPError } from '@utils/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { postAddress, validateAddress, getBossStoreSelected, postAddressBoss} from '@utils/services';

import useRedirectOnEmptyCart from '@hooks/useRedirectOnEmptyCart';
import useFormattedOrderItems from '@hooks/useFormattedOrderItems';

import CheckoutHeader from '@components/CheckoutHeader';
import ProcessStepTracker from '@components/ProcessStepTracker';
import ShippingStep from '@components/ShippingStep';
import PaymentStep from '@components/PaymentStep';
import CartProductResume from '@components/CartProductResume';
import AddressCard from '@components/AddressCard';
import CartLoader from '@components/CartLoader';
import SimpleElementSkeleton from '@components/skeletons/SimpleElementSkeleton';
import SessionModal from '@components/SessionModal';
import GenericErrorModal from '@components/GenericErrorModal';
import InstalmentsModal from '@components/InstalmentsModal';

import { getCartForAddressId } from '@utils/services';

import ArrowDown from '@images/svg-icons/arrow_down.svg';
import ArrowUp from '@images/svg-icons/arrow_up.svg';
import useTealiumData from '@hooks/useTealiumData';
import useMonetate from '@hooks/useMonetate';
import useWunderkind from '@hooks/useWunderkind';
import useScrollTop from '@hooks/useScrollTop';


const CheckoutPage = () : ReactElement => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    //get query string from the url
    const { search } = useLocation();

    //get variables from redux store
    const config = useSelector(state => state.cart.config);
    const cart = useSelector(state => state.cart.cart);
    const isLoading = useSelector(state => state.cart.isLoading);
    const formattedOrderItems = useSelector(state => state.cart.formattedOrderItems);
    const selectedShippingMode = useSelector(state => state.cart.selectedShippingMode);
    const isMobile = useSelector(state => state.cart.isMobile);
    const address = useSelector(state => state.cart.address);
    const showSessionModal = useSelector(state => state.cart.showSessionModal);
    const showGenericErrorModal = useSelector(state => state.cart.showGenericErrorModal);
    const espotFooter = useSelector(state => state.cart.espotFooter);
    const isAddressLoaded = useSelector(state => state.cart.isAddressLoaded);
    const isBossSelected = useSelector((state) => state.cart.isBossSelected);

    //getStepNumber - this function is used to determine which page must be shown
    const getStepNumber = () => {
       return window.location.pathname === config.pagesUrl.shipping ? 0 : 1;
    }

    //define state variables for the page
    const [stepNumber, setStepNumber] = useState<number>(getStepNumber());
    const [showCartResume, setShowCartResume] = useState<boolean>(!isMobile);
    const [addressErrors, setAddressErrors] = useState('');
    const [showInstalmentsModal, setShowInstalmentsModal] = useState(false);
    const [storeBossSelected, setStoreBossSelected] = useState(null);

    //this hook is called every time the pathname changes, get the step number and update cart
    useEffect(() => {
        setStepNumber(getStepNumber());
        dispatch(actions.getCart(config.mockRequests, config.hostname));
    }, [window.location.pathname]);

    //this hook is called every time the cart updates, and update the user address
    useEffect(() => {
        if(!!cart) {
            dispatch(actions.getAddress(config.mockRequests, config.hostname, cart.orderId));

            if (!!cart.orderItem && !!cart.orderItem[0] && cart.orderItem[0].xitem_storeIdentifier) {
                getBossStoreSelected(
                  config.mockRequests,
                  config.hostname,
                  config.storeId,
                  cart.orderItem[0].xitem_storeIdentifier
                )
                  .then((res: any) => {
                    if (res) {
                      setStoreBossSelected(res.PhysicalStore);
                      dispatch(actions.setIsBossSelected(true));
                    }
                  })
                  .catch(err => {
                    console.error(
                      '>>> Error on get Store information for BOSS => error status:',
                      err.status,
                      ', error: ',
                      err
                    );
                    handleHTTPError(dispatch, actions, err);
                  });
              }
        }        
    }, [cart]);

    useEffect(() => {
        if((!address && isAddressLoaded === true) || isAddressLoaded === false) {
            navigate(config.pagesUrl.shipping + search);
        } 
    }, [address, isAddressLoaded]);

    //this hook is called every time the stepNumber changes
    useEffect(() => {
        setAddressErrors('');
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
          });
    }, [stepNumber]);

    //hook to scroll top the page
    useScrollTop();

    //analyzeAddess - control and format address object to send with address calls
    const analyzeAddress = (data: any, addressId: string) => {        
        let addressOrNickname = !!addressId && addressId.length > 0 ?
        {
            'addressId': addressId
        } :
        {
            /* @ts-expect-error */
            'nickName': cart.buyerId + parseInt((Math.random()*1000000000000))
        };

        const newDate = new Date(data['deliveryBirthday']);
        const month = (newDate.getMonth() + 1).toString();
        const monthRight = month.length === 1 ? '0'+ month : month;
        const day = newDate.getDate().toString().length === 1 ? '0' + newDate.getDate() : newDate.getDate().toString();
        const birthday = newDate.getFullYear() + '-' + monthRight + '-' + day;

        const addressType = data['use-address-for-billing'] ? 
            {} :
            {
                "addressType": "Shipping"
            };

        const userId = config.onBehalfSession === true && !!cart && !!cart.buyerId ? {
            'userId': cart.buyerId
        } : {};

        const deliveryAddress =  {
            "storeId": config.storeId,
            "email1": data['deliveryEmail'],
            "firstName": data['deliveryFirstName'],
            "lastName": data['deliveryLastName'],
            "address1": data['deliveryAddress'],
            "address2": data['deliveryAddress2'],
            "city": data['deliveryCity'],
            "state": data['deliveryState'],
            "zipCode": data['deliveryZipCode'],
            "phone1": data['deliveryPhone'],
            "country": "US",
            "DATE_OF_BIRTH": birthday,
            "newsletter": true,
            "sms": data['subscribe-sms'],
            ...addressOrNickname,
            ...addressType,
            orderId: cart.orderId,
            ...userId
        };

        if(data['use-address-for-billing'] === true) {
            return {
                "deliveryAddress": deliveryAddress, 
                "billingAddress": deliveryAddress
            };
        }

        const addressOrNicknameBilling = !!addressId && addressId.length > 0 ? 
                {
                    'addressId': addressId
                } :
                {
                    /* @ts-expect-error */
                    'nickName': cart.buyerId + parseInt((Math.random()*1000000000000))
                };

        const billingAddress = {
            "storeId": config.storeId,
            "email1": data['deliveryEmail'],
            "firstName": data['billingFirstName'],
            "lastName": data['billingLastName'],
            "address1": data['billingAddress'],
            "address2": data['billingAddress2'],
            "city": data['billingCity'],
            "state": data['billingState'],
            "zipCode": data['billingZipCode'],
            "phone1": data['deliveryPhone'],
            "country": "US",
            "DATE_OF_BIRTH": birthday,
            "newsletter": true,
            ...addressOrNicknameBilling,
            "addressType": "Billing",
            orderId: cart.orderId,
            ...userId
        }

        return {
            "deliveryAddress": deliveryAddress, 
            "billingAddress": billingAddress
        };
    }

    //callValidateAddress - call service for validate address
    const callValidateAddress = (addressId: string, customBillingAddressId: string, customOrderId: string, address: object) => {
        setAddressErrors(''); //PULISCO TUTTI GLI ERRORI PER UNA NUOVA VALIDAZIONE
        const orderId = config.onBehalfSession === true ? {
            'orderId': customOrderId
        } : {};

        validateAddress(config.hostname,
            {
                "isOrderItemAddressShipMethodUpdateGL": "true",
                "calculationUsage": "-1,-2,-3,-4,-5,-6,-7",
                "addressId": addressId, 
                "customBillingAddressId": customBillingAddressId, 
                "customOrderId": customOrderId,
                ...orderId
            }
        ).then((res: any) => {
            if(res.status === 200) {
                dispatch(actions.setAddress(address));
                dispatch(actions.getCart(config.mockRequests, config.hostname));
                dispatch(actions.startLoading());
                if(typeof $TB != 'undefined' && !!$TB.hooks) {
                    $TB.hooks.fireEvent("pageChange", null, {"sendBeacon": true});
                }
                navigate(config.pagesUrl.payment + search);
            }
        }).catch(err => {
            console.error('>>> Validate address => error status:', err.status, ', error: ', err);
            if(err.status === 401 || err.status === 403) {
                dispatch(actions.setShowSessionModal(true));
            }

            if(err.status === 400) {
                if(err.data.errors[0].errorMessage.toLowerCase().search('address') !== -1){
                    setAddressErrors('all');
                } else {
                    if(err.data.errors[0].errorMessage.toLowerCase().search('zip code') !== -1 || 
                    err.data.errors[0].errorMessage.toLowerCase().search('zipcode') !== -1) {
                        setAddressErrors('zipCode');
                    } 
                        
                    if(err.data.errors[0].errorMessage.toLowerCase().search('city') !== -1) {
                        setAddressErrors('city');
                    }
    
                    if(err.data.errors[0].errorMessage.toLowerCase().search('state') !== -1) {
                        setAddressErrors('state');
                    }                    
                    if(err.data.errors[0].errorMessage.toLowerCase().search('puertorico_stella') !== -1) {
                        setAddressErrors('puertorico_stella');
                    }
                    if(err.data.errors[0].errorMessage.toLowerCase().search('stella_excluded') !== -1) {
                        setAddressErrors('stella_excluded');
                    }
                }                  
            }

            if(err.status !== 400 && err.status !== 401 && err.status !== 403) {
                dispatch(actions.setGenericErrorModal(true, getLabelFallback('GENERIC_ERROR_MODAL_TEXT_LINE')));
            }

            dispatch(actions.errorLoading());
        })
    }

    //onSubmitAddress - call
    const onSubmitAddress = (data) => {
        if (selectedShippingMode.shipModeCode != "Ship to store" && selectedShippingMode.shipModeDescription != "Collect in store"){
        dispatch(actions.startLoading());

        let addressId = '';
        getCartForAddressId(config.hostname, config.hostname)
            .then((res: any) => {
                addressId = !!res && !!res.data && !!res.data.orderItem && !!res.data.orderItem[0] ? res.data.orderItem[0].addressId : '';
            })
            .catch(err => {
                console.error('>>> Get updated card for address => error status:', err.status, ', error: ', err);
                handleHTTPError(dispatch, actions, err);
            });

        let address = analyzeAddress(data, addressId);
        dispatch(actions.setEmail(data['deliveryEmail']));

        const sameAddress = data['use-address-for-billing'];
        if(!!config.form && config.form.dob_field === false) {
            address.deliveryAddress['DATE_OF_BIRTH'] = '';
            address.billingAddress['DATE_OF_BIRTH'] = '';
        }

        postAddress(config.hostname, address.deliveryAddress)
            .then( (res: any) => {
                if(sameAddress === false) {
                    /* @ts-expect-error */
                    address.deliveryAddressId = res.data.addressId[0];

                    postAddress(config.hostname, address.billingAddress)
                        .then((res1: any) => {
                            if(res1.status === 200) {
                                /* @ts-expect-error */
                                address.billingAddressId = res1.data.addressId[0];
                                callValidateAddress(res.data.addressId[0], res1.data.addressId[0], cart.orderId, address);
                            }                    
                        })
                        .catch(err => {
                            console.error('>>> Post billing address => error status:', err.status, ', error: ', err);
                            if(err.status === 401 || err.status === 403) {
                                dispatch(actions.setShowSessionModal(true));
                            }

                            if(err.status === 400) {
                                if(err.data.errors[0].errorMessage.toLowerCase().search('nickname') !== -1){
                                    setAddressErrors('nickname');
                                } else {
                                    setAddressErrors('generic');
                                }                  
                            }

                            if(err.status !== 400 && err.status !== 401 && err.status !== 403) {
                                dispatch(actions.setGenericErrorModal(true, getLabelFallback('GENERIC_ERROR_MODAL_TEXT_LINE')));
                            }
                            
                            dispatch(actions.errorLoading());
                        })
                } else {
                    if(res.status === 200) {
                        /* @ts-expect-error */
                        address.deliveryAddressId = res.data.addressId[0];

                        /* @ts-expect-error */
                        address.billingAddressId = null;
                        callValidateAddress(res.data.addressId[0], res.data.addressId[0], cart.orderId, address);
                    }                
                }
            })
            .catch(err => {
                console.error('>>> Post delivery address => error status:', err.status, ', error: ', err);
                if(err.status === 401 || err.status === 403) {
                    dispatch(actions.setShowSessionModal(true));
                }

                if(err.status === 400) {
                    if(err.data.errors[0].errorMessage.toLowerCase().search('nickname') !== -1){
                        setAddressErrors('nickname');
                    } else {
                        setAddressErrors('generic');
                    }                  
                }

                if(err.status !== 400 && err.status !== 401 && err.status !== 403) {
                    dispatch(actions.setGenericErrorModal(true, getLabelFallback('GENERIC_ERROR_MODAL_TEXT_LINE')));
                }
                
                dispatch(actions.errorLoading());
            })
        }else{
            dispatch(
                actions.setGenericErrorModal(
                  true,
                  getLabelFallback('ADDRESS_ERROR_MODAL_TEXT_LINE')
                )
              );
        }
    }

    //On submit boss address
    const onSubmitBossAddress = (data) => {
        getBossStoreSelected(config.mockRequests, config.hostname, config.storeId, cart.orderItem[0].xitem_storeIdentifier)
        .then((res: any) => {
          if (res) {
            let bossStore = res.PhysicalStore;
            dispatch(actions.setEmail(data['deliveryEmail']));
            if (cart.orderItem[0].xitem_storeIdentifier){
            postAddressBoss(config.hostname, config.storeId, {
              orderId: cart.orderId,
              catalogId: config.catalogId,
              langId: config.langId,
              authToken: '',//config.authToken,
              /* @ts-expect-error */
              nickName: cart.buyerId + parseInt(Math.random() * 1000000000000),
              bossStore: cart.orderItem[0].xitem_storeIdentifier,
              email1: data.deliveryEmail,
              phone1: data.deliveryPhone,
              shippingFirstName: data.deliveryFirstName,
              shippingLastName: data.deliveryLastName,
              shippingAddress1: bossStore[0].addressLine[0],
              shippingAddress2: '',
              shippingCity: bossStore[0].city,
              shippingCountry: bossStore[0].country,
              shippingState: bossStore[0].stateOrProvinceName,
              shippingZipCode: bossStore[0].postalCode,
              billingFirstName: data.billingFirstName,
              billingLastName: data.billingLastName,
              billingAddress1: data.billingAddress,
              billingAddress2: !!data.billingAddress2 ? data.billingAddress2 : '',
              billingCity: data.billingCity,
              billingCountry: !!data.billingCountry ? data.billingCountry : 'US',
              billingState: data.billingState,
              billingZipCode: data.billingZipCode,
              sms: data['subscribe-sms'],
            })
              .then((res: any) => {
                if (res.status === 200) {
                  //dispatch(actions.setAddress(address));
                  dispatch(actions.getCart(config.mockRequests, config.hostname));
                  if(typeof $TB != 'undefined' && !!$TB.hooks) {
                    $TB.hooks.fireEvent("pageChange", null, {"sendBeacon": true});
                  }
                  navigate(config.pagesUrl.payment + search);
                }
              })
              .catch(err => {
                console.error(
                  '>>> Post Boss address => error status:',
                  err.status,
                  ', error: ',
                  err
                );
                if (err.status === 401 || err.status === 403) {
                  dispatch(actions.setShowSessionModal(true));
                }

                if (err.status === 400) {
                  if (err.data.errors[0].errorMessage.toLowerCase().search('nickname') !== -1) {
                    setAddressErrors('nickname');
                  } else {
                    setAddressErrors('generic');
                  }
                }

                if (err.status !== 400 && err.status !== 401 && err.status !== 403) {
                  dispatch(
                    actions.setGenericErrorModal(
                      true,
                      getLabelFallback('GENERIC_ERROR_MODAL_TEXT_LINE')
                    )
                  );
                }

                dispatch(actions.errorLoading());
              });
            }else{
                dispatch(
                    actions.setGenericErrorModal(
                      true,
                      getLabelFallback('ADDRESS_ERROR_MODAL_TEXT_LINE')
                    )
                  );
            }
          }
        })
        .catch(err => {
          console.error(
            '>>> Error on get Store information for BOSS => error status:',
            err.status,
            ', error: ',
            err
          );
          handleHTTPError(dispatch, actions, err);
        });
    }

    //define sizes for images
    const sizes = {
        mobile: {
            width: '145px',
            height: '94px'
        },
        desktop:{
            width: '206px',
            height: '137px'
        }
    };

    //getCartProduct - for each item in the cart, create CartProductResume tile
    const getCartProduct = (orderItem) => {
        return orderItem.map((item, index) => {
            return <CartProductResume 
                key={index} item={item} 
                sizes={sizes}
                prescriptionPosition='bottom'/>
        })
    }

    useEffect(() => {        
        setShowCartResume(!isMobile);
    }, [isMobile]);

    //handleBack - handle click on 'Back' link 
    const handleBack = (goTo: number) => {
        dispatch(actions.startLoading());
        if(goTo === -1) {
            dispatch(actions.setClientSideNavigation(true));
            navigate(config.pagesUrl.cart + search);
        } else {
            if(typeof $TB != 'undefined' && !!$TB.hooks) {
                $TB.hooks.fireEvent("pageChange", null, {"sendBeacon": true});
            }
            navigate(config.pagesUrl.shipping + search);
        }
    }

    //this hook, at first render call the function to show the custom footer
    useEffect(() => {
        config.callFromComponentToGl.hideHeaderFooter(true);
    }, []);

    //this hook, call the footer espot service every time the config changes
    useEffect(() => {
        dispatch(actions.getEspotFooter(config.hostname));
    }, [config]);

    //hook for redirect to cart page on empty cart
    useRedirectOnEmptyCart();

    //hook for retrieve data to show
    useFormattedOrderItems(cart); 

    //hook for set analytics data
    useTealiumData(stepNumber === 0 ? 'Delivery' : 'Payment', cart);
    
    useMonetate(stepNumber === 0 ? 'Shipping' : 'Payment', cart);

    useWunderkind(stepNumber === 0 ? 'Shipping' : 'Payment', cart);



    return (
        <>
            <CheckoutHeader handleBack={() => handleBack(stepNumber === 0 ? -1 : 0)} stepNumber={stepNumber}/>
            <section className="checkout-page-container">
                <div className="step-tracker-container">
                    <ProcessStepTracker stepNumber={stepNumber}/>
                </div>
                    
                <div className="checkout-page">
                    <div className="checkout-page-left">
                    { stepNumber === 0 && <ShippingStep onSubmitAddress={(data) => onSubmitAddress(data)} onSubmitBossAddress={(data) => onSubmitBossAddress(data)} addressErrors={addressErrors}/> }
                        { stepNumber === 1 && !!address && <PaymentStep handleBack={() => handleBack(0)}/> }                   
                    </div>
                            
                    <div className="checkout-page-right">
                        <div>
                            <div className="checkout-page-right-summary" onClick={() => setShowCartResume(!showCartResume)}>
                                <label className="checkout-page-right-summary-left">{ getLabelFallback('SHOW_SUMMARY_CART_LABEL') } </label>                                 
                                <label className="checkout-page-right-summary-right">
                                    {!!cart && getLabelFallback("CURRENCY_LABEL") }
                                    {!!cart ? convertNumber(cart.grandTotal) : <SimpleElementSkeleton width={'90px'} height={'24px'} />}
                                    <span className="checkout-page-right-summary-icon" >
                                        {!showCartResume ? <ArrowDown /> : <ArrowUp />}
                                    </span>
                                </label>
                            </div>                       
                        </div>
                        <div className="checkout-page-right-main">
                            {(isMobile === false || (showCartResume === true && isMobile === true)) && 
                                <>
                                    
                                    <div className="checkout-page-right-content">
                                        {!!formattedOrderItems ?
                                            <>{ getCartProduct(formattedOrderItems) }</> :
                                            <div style={{marginBottom: '16px'}}><SimpleElementSkeleton width={"100%"} height={"400px"} /></div>
                                        }
                                    </div>

                                    <div className="checkout-page-right-resume">
                                        <div className="checkout-page-right-resume-shipping">
                                            <div className="checkout-page-right-resume-shipping-header">
                                                <label>{getLabelFallback('CART_RESUME_SHIPPING_METHOD_LABEL')}:</label>
                                                <button className="primary-button" data-element-id='Summary_EditShippingMethod' onClick={() => handleBack(-1)}>{getLabelFallback('CART_RESUME_SHIPPING_EDIT_LABEL')}</button>
                                            </div>
                                            <div className="checkout-page-right-resume-shipping-type">
                                                <label>
                                                    {!!selectedShippingMode && !!selectedShippingMode.shipModeDescription && selectedShippingMode.shipModeDescription.toLowerCase() === 'speedy rx' && getLabelFallback('SPEEDY_RX_DETAIL_TEXT')}
                                                    {!!selectedShippingMode && !!selectedShippingMode.shipModeDescription && selectedShippingMode.shipModeDescription.toLowerCase() !== 'speedy rx' && selectedShippingMode.shipModeDescription}
                                                </label>
                                                <label>{!!selectedShippingMode && !!selectedShippingMode.shipModeDescription && selectedShippingMode.shipModeDescription.toLowerCase() !== 'speedy rx' && (selectedShippingMode.field2 === '0.00' ? getLabelFallback('CART_RESUME_FREE_LABEL') : (getLabelFallback('CURRENCY_LABEL') + selectedShippingMode.field2 ))}</label>
                                            </div>
                                            {isBossSelected && !!storeBossSelected && !!storeBossSelected[0] &&
                                                <div className="checkout-page-right-resume-shipping-store">
                                                    <div className="checkout-page-right-resume-shipping-store-title">
                                                        {storeBossSelected[0].Description[0].displayStoreName.toLowerCase().replace('lenscrafters', 'LensCrafters')}
                                                    </div>
                                                    <div className="checkout-page-right-resume-shipping-store-address">
                                                        {storeBossSelected[0].addressLine[0].toLowerCase()}{' '}
                                                        {storeBossSelected[0].city.toLowerCase()}, {storeBossSelected[0].stateOrProvinceName}
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                        <div className="checkout-page-right-resume-order">
                                            <div className="checkout-page-right-resume-order-subtotal">
                                                <label className="first-label">{getLabelFallback('CART_RESUME_SUBTOTAL_LABEL')}</label>
                                                {!!cart ?
                                                    <label>{getLabelFallback('CURRENCY_LABEL')}{getSubtotal(cart)}</label> :
                                                    <label><SimpleElementSkeleton width={'100px'} height={'24px'} /></label>
                                                }
                                            </div>
                                            <div className="checkout-page-right-resume-order-tax">
                                                <label className="first-label">{getLabelFallback('CART_RESUME_TAX_LABEL')}</label>
                                                {!!cart ? 
                                                    <label>{getTotalTax(cart)}</label> :
                                                    <label><SimpleElementSkeleton width={'100px'} height={'24px'} /></label>
                                                }
                                            </div>
                                            <div className="checkout-page-right-resume-order-total">
                                                <label className="first-label">{getLabelFallback('CART_RESUME_TOTAL_LABEL')}</label>
                                                {!!cart ?
                                                    <label>{getLabelFallback('CURRENCY_LABEL')}{convertNumber(cart.grandTotal)}</label>:
                                                    <label><SimpleElementSkeleton width={'100px'} height={'24px'} /></label>
                                                }
                                            </div>
                                            <div className="checkout-page-right-resume-instalments">
                                                {
                                                    getLabelFallback('RESUME_INSTALMENTS_LABEL')
                                                    .concat(config.klarna.enabled === true ? ' and Klarna' : '')
                                                    .concat('. ')
                                                } 
                                                <button aria-label={ getLabelFallback('LEARN_MORE_RESUME_INSTALMENTS_LABEL') }
                                                    className="primary-button" 
                                                    onClick={() => {setShowInstalmentsModal(true)}}
                                                    data-element-id={stepNumber === 0 ? 'Delivery_PayOverTime':'Payment_PayOverTime' }
                                                >
                                                    { getLabelFallback('LEARN_MORE_PAYMENT_LABEL') }
                                                </button>

                                                {showInstalmentsModal && 
                                                    <InstalmentsModal close={ () => setShowInstalmentsModal(false)}/>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }

                            {(isMobile === false) && stepNumber > 0 && !!address && 
                                <div className="checkout-page-right-address">
                                    <AddressCard 
                                        cardTitle={isBossSelected ? getLabelFallback('ADDRESS_CARD_BOSS_TITLE') : getLabelFallback('ADDRESS_CARD_DELIVERY_TITLE')}
                                        address={address.deliveryAddress}
                                        linkLabel={getLabelFallback('EDIT_ADDRESS_LABEL')}
                                        onClick={() => handleBack(0)}
                                        isBoss={isBossSelected}
                                    />

                                    <AddressCard 
                                        cardTitle={getLabelFallback('ADDRESS_CARD_BILLING_TITLE')}
                                        address={address.billingAddress}
                                        linkLabel={getLabelFallback('EDIT_ADDRESS_LABEL')}
                                        onClick={() => handleBack(0)}
                                        isBoss={false}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </div>
                    
                {(isMobile === true) && stepNumber > 0 && !!address && 
                    <div className="checkout-page-bottom">
                        <div className="checkout-page-bottom-container">
                            <AddressCard 
                                cardTitle={isBossSelected ? getLabelFallback('ADDRESS_CARD_BOSS_TITLE') : getLabelFallback('ADDRESS_CARD_DELIVERY_TITLE')}
                                address={address.deliveryAddress}
                                linkLabel={getLabelFallback('EDIT_ADDRESS_LABEL')}
                                onClick={() => handleBack(0)}
                                isBoss={isBossSelected}
                            />

                            <AddressCard 
                                cardTitle={getLabelFallback('ADDRESS_CARD_BILLING_TITLE')}
                                address={address.billingAddress}
                                linkLabel={getLabelFallback('EDIT_ADDRESS_LABEL')}
                                onClick={() => handleBack(0)}
                                isBoss={false}
                            />
                        </div>
                    </div>
                }

                {showCartResume === true && isMobile === true && 
                    <div className="disabling"></div>
                }
            </section>

            {isLoading &&
                <CartLoader />
            }
            {
                showSessionModal && <SessionModal />
            }
            {
                showGenericErrorModal && <GenericErrorModal />
            }
            {espotFooter && <footer dangerouslySetInnerHTML={{__html: espotFooter}} />}
        </>
    );
}

export default CheckoutPage;